import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MapPin, Maximize, Tag, Check } from 'lucide-react';
import { localize, formatPrice, CATEGORIES } from '../data/mock';
import { fetchProperty, fetchProperties } from '../data/api';
import { useLang } from '../context/LanguageContext';
import PropertyCard from '../components/PropertyCard';
import Reveal from '../components/Reveal';
import { CONTACT_INFO } from '../components/Footer';

const PropertyDetail = () => {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setActiveImg(0);
    fetchProperty(id).then((p) => {
      if (!active) return;
      setProperty(p);
      setLoading(false);
      if (p) {
        fetchProperties({ category: p.category }).then((list) => {
          if (active) setSimilar(list.filter((x) => x.id !== p.id).slice(0, 3));
        });
      }
    });
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4efe6]">
        <div className="w-10 h-10 border-2 border-[#b0894f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4efe6]">
        <div className="text-center">
          <p className="font-serif-display text-3xl text-[#1b2331] mb-6">404</p>
          <Link to="/biens" className="font-sans-elegant text-xs tracking-wide-2 uppercase text-[#b0894f] link-underline">
            {t('detail.back')}
          </Link>
        </div>
      </div>
    );
  }

  const p = property;
  const cat = CATEGORIES.find((c) => c.slug === p.category);

  const specs = [
    { icon: Tag, label: t('detail.reference'), value: p.ref },
    { icon: MapPin, label: t('detail.location'), value: localize(p.location, lang) },
    p.surface && { icon: Maximize, label: t('detail.surface'), value: `${p.surface.toLocaleString(lang === 'en' ? 'en-US' : 'fr-FR')} m²` },
  ].filter(Boolean);

  return (
    <div className="bg-[#f4efe6]">
      {/* Gallery */}
      <section className="pt-24">
        <div className="relative h-[62vh] md:h-[78vh] overflow-hidden bg-[#eae2d3]">
          <img
            src={p.images[activeImg]}
            alt={localize(p.title, lang)}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1b2331]/50 to-transparent" />

          <button
            onClick={() => navigate(-1)}
            className="absolute top-6 left-6 flex items-center gap-2 bg-[#f4efe6]/90 backdrop-blur-sm text-[#1b2331] font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase px-4 py-2.5 hover:bg-[#1b2331] hover:text-[#f4efe6] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('detail.back')}
          </button>

          {p.images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {p.images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`h-1 transition-all duration-300 ${activeImg === i ? 'w-10 bg-[#c8a469]' : 'w-5 bg-[#f4efe6]/60'}`}
                  aria-label={`Image ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="bg-[#1b2331] text-[#f4efe6] font-sans-elegant text-[0.6rem] tracking-wide-2 uppercase px-3 py-1.5">
                  {cat ? localize(cat.label, lang) : ''}
                </span>
                <span className="font-sans-elegant text-[0.62rem] tracking-brand uppercase text-[#b0894f]">
                  {p.country}
                </span>
              </div>

              <h1 className="font-serif-display text-4xl md:text-6xl text-[#1b2331] font-light leading-tight mb-4">
                {localize(p.title, lang)}
              </h1>
              <p className="font-sans-elegant text-sm tracking-wide-2 uppercase text-[#8a8d93] flex items-center gap-2 mb-10">
                <MapPin className="w-4 h-4 text-[#b0894f]" />
                {localize(p.location, lang)}
              </p>

              <div className="hairline w-16 mb-8" />

              <h2 className="font-sans-elegant text-[0.7rem] tracking-brand uppercase text-[#b0894f] mb-5">
                {t('detail.overview')}
              </h2>
              <p className="font-sans-elegant text-lg text-[#5a5d63] leading-relaxed mb-12">
                {localize(p.description, lang)}
              </p>

              <h2 className="font-sans-elegant text-[0.7rem] tracking-brand uppercase text-[#b0894f] mb-6">
                {t('detail.features')}
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {localize(p.features, lang).map((f, i) => (
                  <div key={i} className="flex items-center gap-3 border-b border-[#1b2331]/10 pb-3">
                    <Check className="w-4 h-4 text-[#b0894f] shrink-0" />
                    <span className="font-sans-elegant text-sm text-[#1b2331]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right sticky card */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <div className="bg-[#1b2331] text-[#f4efe6] p-8 md:p-10">
                  <p className="font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase text-[#c8a469] mb-2">
                    {t('detail.price')}
                  </p>
                  <p className="font-serif-display text-4xl md:text-5xl mb-8">
                    {p.price ? formatPrice(p.price, lang) : t('detail.onRequest')}
                  </p>

                  <div className="space-y-5 border-t border-[#f4efe6]/15 pt-8">
                    {specs.map((s, i) => (
                      <div key={i} className="flex items-center justify-between gap-4">
                        <span className="flex items-center gap-2.5 font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase text-[#f4efe6]/60">
                          <s.icon className="w-4 h-4 text-[#c8a469]" />
                          {s.label}
                        </span>
                        <span className="font-sans-elegant text-sm text-right">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#eae2d3] p-8 md:p-10 mt-4">
                  <h3 className="font-serif-display text-2xl text-[#1b2331] mb-3 leading-tight">
                    {t('detail.interested')}
                  </h3>
                  <p className="font-sans-elegant text-sm text-[#5a5d63] leading-relaxed mb-6">
                    {t('detail.interestedText')}
                  </p>
                  <Link
                    to={`/contact?ref=${p.ref}`}
                    className="group flex items-center justify-center gap-3 bg-[#1b2331] text-[#f4efe6] font-sans-elegant text-xs tracking-wide-2 uppercase px-6 py-4 hover:bg-[#b0894f] transition-colors w-full"
                  >
                    {t('detail.contactBtn')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a
                    href={`tel:${CONTACT_INFO.tel.replace(/\s/g, '')}`}
                    className="block text-center font-sans-elegant text-sm text-[#1b2331] mt-4 link-underline mx-auto w-fit"
                  >
                    {CONTACT_INFO.tel}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Similar */}
      {similar.length > 0 && (
        <section className="py-16 md:py-24 bg-[#eae2d3]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1b2331] font-light mb-14">
              {t('detail.similar')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {similar.map((s, i) => (
                <Reveal key={s.id} delay={i * 100}>
                  <PropertyCard property={s} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PropertyDetail;
